import React, { useEffect, useRef, useState } from "react";
import { Button, Modal, Form } from "react-bootstrap";
import styles from "./Output.module.css";
import { unparse } from "papaparse";
import API from "../axios";
import { getUiGroup, isKnownSpecKey, getDisplayLabel, COMPARE_UI_GROUP_ORDER } from "./compareSpecHelpers";

export interface AuditResult {
    isCorrect: boolean;
    confidence: "high" | "medium" | "low";
    recommendedValue: string | null;
    reasoning: string;
    sourceUrls: { url: string; title?: string }[];
    rowInfo: { row: TableRow; index: number; isEnumerated: boolean; currentValue: string; expectedValues: string[] };
    onAccept: () => void;
    onReject: () => void;
    onSaveAsOption: (value: string, andAccept?: boolean) => void;
    onAddKnownValue: (value: string) => void;
}

export interface TableRow {
    rawMainCategory: string;
    rawSubCategory: string;
    rawSpecName: string;

    mainCategory: string;
    subCategory: string;
    specName: string;
    uiGroup: string;

    value: any;
    rating: any;

    searchLoading: boolean;
    showEdit: boolean;
}

interface StoredAuditData {
    isCorrect: boolean;
    confidence: string;
    recommendedValue: string | null;
    reasoning: string;
    sourceUrls: { url: string; title?: string }[];
    isEnumerated: boolean;
    currentValue: string;
    expectedValues: string[];
}

const SKIP_AUDIT_SPECS = new Set(["make", "model", "production_year", "trim_level"]);

const DIMENSION_SNs = new Set(["length", "length_(mm)", "width", "width_(mm)", "height", "height_(mm)", "wheelbase", "wheelbase_(mm)"]);

const PERFORMANCE_SNs = new Set(["horse_power", "horse_power(hp)", "torque", "torque_(n.m)"]);

function normalizeRecommendedValue(value: string | null, isEnumerated: boolean): string | null {
    if (!value || value.includes("|") || !isEnumerated) return value;
    const parts = value.split(",").map((v) => v.trim()).filter(Boolean);
    return parts.length > 1 ? parts.join(" | ") : value;
}

interface ComputedScores {
    general: number;
    performance: number;
    safety: number;
    features: number;
    overall: number;
    customer: number;
}

interface MatchSpecificationOutputProps {
    result: any;
    vehicleId: string;
    loading: boolean;
    readOnly?: boolean;
    onResultChange?: (spec: object) => void;
    onRowFocus?: (row: TableRow, index: number) => void;
    onSearchComplete?: (row: TableRow, value: string, sourceUrls: { url: string; title?: string }[]) => void;
    onAuditChange?: (auditedCount: number, totalCount: number) => void;
    allRatings?: any[];
    expectedResults?: any[];
    onRatingCreated?: (rating: any) => void;
    onExpectedResultUpdated?: (updatedEr: any) => void;
    onAuditStart?: (rowInfo: AuditResult["rowInfo"]) => void;
    onAuditResult?: (result: AuditResult) => void;
    vehicleTitle?: string;
    isAudited?: boolean;
    onScoresComputed?: (scores: ComputedScores) => void;
}

const MatchSpecificationOutput = (props: MatchSpecificationOutputProps) => {
    const internalUpdate = useRef(false);
    const pendingAcceptRef = useRef<{ index: number; row: TableRow; value: string } | null>(null);
    const resultRef = useRef<any>(props.result);
    const auditStopRef = useRef(false);
    const generateCallIdRef = useRef(0);
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [make, setMake] = useState("");
    const [model, setModel] = useState("");
    const [productionYear, setProductionYear] = useState("");
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [editingIndex, setEditingIndex] = useState<number | null>(null);
    const [editingText, setEditingText] = useState("");
    const [editingRating, setEditingRating] = useState("");
    const [addNewModal, setAddNewModal] = useState<{ row: TableRow; index: number } | null>(null);
    const [newOptionValue, setNewOptionValue] = useState("");
    const [newOptionRating, setNewOptionRating] = useState("");
    const [addingNewLoading, setAddingNewLoading] = useState(false);

    const [tableData, setTableData] = useState<TableRow[]>([]);
    const [processing, setProcessing] = useState(false);
    const [focusedRowIndex, setFocusedRowIndex] = useState<number | null>(null);
    const [auditedIndices, setAuditedIndices] = useState<Set<number>>(new Set());
    const [auditingIndices, setAuditingIndices] = useState<Set<number>>(new Set());
    const [auditAllRunning, setAuditAllRunning] = useState(false);
    const [auditAllProgress, setAuditAllProgress] = useState({ done: 0, total: 0 });
    const [rowAuditData, setRowAuditData] = useState<Map<number, StoredAuditData>>(new Map());
    const [needsManualAudit, setNeedsManualAudit] = useState<Set<number>>(new Set());

    const [editingScores, setEditingScores] = useState(false);
    const [scoreSaving, setScoreSaving] = useState(false);
    const [editedScores, setEditedScores] = useState({
        general: "",
        performance: "",
        safety: "",
        features: "",
        customerScore: "",
    });
    const [overrideScores, setOverrideScores] = useState<{
        general: number; performance: number; safety: number; features: number;
        overall: number; customer: number;
    } | null>(null);

    const formatString = (input: string) =>
        input
            .split("_")
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(" ");

    const handleStartEditScores = (
        generalScore: number,
        performanceScore: number,
        safetyScore: number,
        featuresScore: number,
        finalScore: number,
    ) => {
        setEditedScores({
            general:       generalScore.toFixed(1),
            performance:   performanceScore.toFixed(1),
            safety:        safetyScore.toFixed(1),
            features:      featuresScore.toFixed(1),
            customerScore: finalScore.toFixed(1),
        });
        setEditingScores(true);
    };

    const handleCancelEditScores = () => {
        setEditingScores(false);
        setEditedScores({ general: "", performance: "", safety: "", features: "", customerScore: "" });
    };

    const handleSaveScores = async () => {
        setScoreSaving(true);
        try {
            const clamp = (v: string) => Math.min(10, Math.max(0, parseFloat(v) || 0));
            const g = clamp(editedScores.general);
            const p = clamp(editedScores.performance);
            const s = clamp(editedScores.safety);
            const f = clamp(editedScores.features);
            const overall = Math.round((g * 0.45 + p * 0.30 + s * 0.08 + f * 0.17) * 100) / 100;
            const customer = clamp(editedScores.customerScore);

            await API.updateVehicle(props.vehicleId, {
                general_rating:        g,
                performance_rating:    p,
                safety_rating:         s,
                features_rating:       f,
                customer_score_rating: customer,
                rating:                Math.round(overall * 100) / 100,
            });
            setOverrideScores({ general: g, performance: p, safety: s, features: f, overall, customer });
            setEditingScores(false);
        } catch (err) {
            console.error("Failed to save scores:", err);
        } finally {
            setScoreSaving(false);
        }
    };

    const getRatings = async () => {
        try {
            const result: any = await API.getAllRatings();
            return Array.isArray(result.data) ? result.data : [];
        } catch {
            return [];
        }
    };

    const getRatingData = (
        ratingDatas: any[],
        key: string,
        value: string | number,
    ) => {
        if (!value || value === "-") return "-";

        let total = 0;
        const values = String(value).split("|");

        const normalizedKey = key.trim().toLowerCase();

        values.forEach((val) => {
            const normalizedVal = val.trim().toLowerCase();
            ratingDatas.forEach((r) => {
                const rKey = String(r.key).trim().toLowerCase();
                const rVal = String(r.value).trim().toLowerCase();
                if (rKey === normalizedKey && rVal === normalizedVal) {
                    total += parseFloat(r.rating) || 0;
                }
            });
        });

        return total > 0 ? total.toFixed(2) : "-";
    };

    const generateDataRowsFromJson = async (data: any) => {
        if (!data || typeof data !== "object") return;
        setProcessing(true);

        // Stamp this invocation so stale concurrent calls don't overwrite newer results
        const callId = ++generateCallIdRef.current;

        const ratingDatas = await getRatings();

        // If a newer call started while we were awaiting, discard this result
        if (callId !== generateCallIdRef.current) return;

        const rows: TableRow[] = [];
        // Keyed by spec name only — prevents the same spec from appearing twice
        // even when the AI places it under a different sub-category or main category.
        const processedSpecs = new Set<string>();

        if (data["1_basic_vehicle_information"]) {
            const basic = data["1_basic_vehicle_information"];
            setMake(basic.vehicle_identity?.make || "");
            setModel(basic.vehicle_identity?.model || "");
            setProductionYear(basic.production_detail?.production_year || "");
        }

        const EMPTY_STRING_VALUES = new Set([
            "false", "null", "undefined", "unavailable",
            "not available", "n/a", "na", "none", "unknown", "-",
        ]);

        const isFalsy = (v: any): boolean => {
            if (v === null || v === undefined || v === false || v === "") return true;
            if (typeof v === "string") return EMPTY_STRING_VALUES.has(v.trim().toLowerCase());
            return false;
        };

        // Helper function to normalize values
        const normalizeValue = (value: any): string => {
            if (isFalsy(value)) return "-";
            if (Array.isArray(value)) {
                const filtered = value.filter((v) => !isFalsy(v));
                return filtered.length > 0 ? filtered.join(", ") : "-";
            }
            return String(value);
        };

        // Process ALL specifications, with special handling for combined ratings
        for (const main in data) {
            for (const sub in data[main]) {
                const subCategoryData = data[main][sub];

                // First, create rows for ALL specs
                for (const spec in subCategoryData) {
                    if (!isKnownSpecKey(spec)) continue;

                    const uniqueKey = spec;

                    if (processedSpecs.has(uniqueKey)) {
                        continue;
                    }

                    const rawValue = subCategoryData[spec];
                    const normalizedValue = normalizeValue(rawValue);

                    let rating: any = "-";
                    const displaySpecName = getDisplayLabel(spec) ?? formatString(spec);

                    // Normalize spec key: lowercase + collapse spaces → underscores
                    // so "length_(mm)", "length", "Length (mm)" all match the same branch.
                    const sn = spec.trim().toLowerCase().replace(/\s+/g, "_");

                    // Dimension specs — segment rating uses all four values together
                    if (sn === "length" || sn === "length_(mm)") {
                        const length   = subCategoryData["length"]   ?? subCategoryData["length_(mm)"];
                        const width    = subCategoryData["width"]    ?? subCategoryData["width_(mm)"];
                        const height   = subCategoryData["height"]   ?? subCategoryData["height_(mm)"];
                        const wheelbase = subCategoryData["wheelbase"] ?? subCategoryData["wheelbase_(mm)"];
                        rating = getSegmentRating(length, width, height, wheelbase);
                    } else if (sn === "width" || sn === "width_(mm)") {
                        rating = getRatingData(ratingDatas, spec, rawValue);
                    } else if (sn === "height" || sn === "height_(mm)") {
                        rating = getRatingData(ratingDatas, spec, rawValue);
                    } else if (sn === "wheelbase" || sn === "wheelbase_(mm)") {
                        rating = getRatingData(ratingDatas, spec, rawValue);

                    // Engine performance — HP rating combines HP + torque
                    } else if (sn === "horse_power" || sn === "horse_power(hp)") {
                        const hp  = subCategoryData["horse_power"]  ?? subCategoryData["horse_power(hp)"];
                        const tq  = subCategoryData["torque"]       ?? subCategoryData["torque_(n.m)"] ?? subCategoryData["torque_(N.M)"];
                        rating = getPerformanceRating(hp, tq);
                    } else if (sn === "torque" || sn === "torque_(n.m)") {
                        rating = getRatingData(ratingDatas, spec, rawValue);

                    // Linear-formula specs
                    } else if (sn === "displacement" || sn.startsWith("displacement")) {
                        rating = getDisplacementRating(rawValue);
                    } else if (sn === "0_100_km_h_acceleration" || sn.includes("0") && sn.includes("100") && sn.includes("acceleration")) {
                        rating = getAccelerationRating(rawValue);
                    } else if (sn === "top_speed" || sn.startsWith("top_speed")) {
                        rating = getTopSpeedRating(rawValue);
                    } else if (sn === "battery_capacity" || sn.startsWith("battery_capacity")) {
                        rating = getBatteryCapacityRating(rawValue);
                    } else if (sn === "fuel_tank_capacity" || sn.startsWith("fuel_tank_capacity")) {
                        rating = getFuelTankRating(rawValue);
                    } else if (sn === "running_cost_per_1km_in_aed" || sn.startsWith("running_cost")) {
                        rating = getRunningCostRating(rawValue);
                    } else if (sn === "number_of_seats" || sn.startsWith("number_of_seats")) {
                        rating = getSeatsRating(rawValue);
                    } else if (sn === "number_of_speakers") {
                        rating = getSpeakersRating(rawValue);
                    } else if (sn === "central_display_size" || sn.startsWith("central_display_size")) {
                        rating = getCentralDisplayRating(rawValue);
                    } else if (sn === "passenger_display_size" || sn.startsWith("passenger_display_size")) {
                        rating = getPassengerDisplayRating(rawValue);
                    } else if (sn === "driver_display_size" || sn.startsWith("driver_display_size")) {
                        rating = getDriverDisplayRating(rawValue);
                    } else if (sn === "number_of_doors") {
                        rating = getDoorsRating(rawValue);
                    } else if (sn === "rim_size" || sn.startsWith("rim_size")) {
                        rating = getRimSizeRating(rawValue);
                    } else if (sn === "tires_types_and_specification") {
                        rating = getTireSpecificationRating(rawValue);
                    } else if (sn === "charging_time_ac" || sn.startsWith("charging_time_ac")) {
                        rating = getChargingTimeAcRating(rawValue);
                    } else if (sn === "charging_time_dc" || sn.startsWith("charging_time_dc") || sn.startsWith("_charging_time_dc")) {
                        rating = getChargingTimeDcRating(rawValue);
                    } else if (sn === "max_charging_power_ac" || sn.startsWith("max_charging_power_ac")) {
                        rating = getMaxChargingPowerAcRating(rawValue);
                    } else if (sn === "max_charging_power_dc" || sn.startsWith("max_charging_power_dc")) {
                        rating = getMaxChargingPowerDcRating(rawValue);
                    } else if (sn === "production_year") {
                        rating = getProductionYearRating(rawValue);
                    } else if (sn === "transmission_gears" || sn.startsWith("transmission_gears")) {
                        rating = getTransmissionGearsRating(rawValue);
                    } else if (sn === "number_of_cameras") {
                        rating = getNumCamerasRating(rawValue);
                    } else if (sn === "number_of_ultrasonic_radars") {
                        rating = getNumUltrasonicRadarsRating(rawValue);
                    } else if (sn === "number_of_lidar_sensors") {
                        rating = getNumLidarSensorsRating(rawValue);
                    } else {
                        rating = normalizedValue !== "-"
                            ? getRatingData(ratingDatas, spec, rawValue)
                            : "-";
                    }

                    rows.push({
                        rawMainCategory: main,
                        rawSubCategory: sub,
                        rawSpecName: spec,

                        mainCategory: formatString(main),
                        subCategory: formatString(sub),
                        specName: displaySpecName,
                        uiGroup: getUiGroup(main, sub, spec),

                        value: normalizedValue,
                        rating: rating,

                        searchLoading: false,
                        showEdit: false,
                    });

                    processedSpecs.add(uniqueKey);
                }
            }
        }

        // Sort rows by main category order
        if (rows.length > 0) {
            const sorted = rows.sort((a, b) => {
                const numA =
                    parseInt(a.rawMainCategory.split("_")[0], 10) || 999;
                const numB =
                    parseInt(b.rawMainCategory.split("_")[0], 10) || 999;
                return numA - numB;
            });
            setTableData(sorted);
        }

        setProcessing(false);
    };

    // Robustly extract the first numeric value from any input type:
    // - strips thousands-separator commas ("1,500" → 1500)
    // - takes the first valid number from an array
    const parseNumericValue = (value: any): number => {
        if (typeof value === "number") return value;
        if (Array.isArray(value)) {
            for (const item of value) {
                const n = parseNumericValue(item);
                if (!isNaN(n)) return n;
            }
            return NaN;
        }
        const cleaned = String(value).replace(/(\d),(\d)/g, "$1$2").trim();
        return parseFloat(cleaned);
    };

    // Generic linear rating helper: weight × MAX(0, MIN((v−min)/(max−min), 1))
    const calcLinearRating = (
        value: string | number | any,
        weight: number,
        min: number,
        max: number,
        invert = false,
    ): string => {
        const v = parseNumericValue(value);
        if (isNaN(v)) return "-";
        const raw = invert ? (min - v) / (min - max) : (v - min) / (max - min);
        const ratio = Math.max(0, Math.min(raw, 1));
        return (weight * ratio).toFixed(2);
    };

    const getDisplacementRating = (v: string | number) => {
        const num = parseFloat(String(v));
        if (isNaN(num)) return 0;
        return (0.31 * Math.max(0, Math.min((num - 0.8) / (6.5 - 0.8), 1))).toFixed(2);
    };

    const getAccelerationRating = (v: string | number) =>
        // lower acceleration time is better → inverted
        calcLinearRating(v, 0.45, 15, 2, true);

    const getTopSpeedRating = (v: string | number) =>
        calcLinearRating(v, 0.26, 120, 500);

    const getBatteryCapacityRating = (v: string | number) =>
        calcLinearRating(v, 0.54, 5, 200);

    const getFuelTankRating = (v: string | number) =>
        calcLinearRating(v, 0.24, 30, 120);

    const getRunningCostRating = (v: string | number) =>
        // lower cost is better → inverted
        calcLinearRating(v, 0.44, 60, 3, true);

    const getSeatsRating = (v: string | number) =>
        calcLinearRating(v, 0.41, 2, 8);

    const getSpeakersRating = (v: string | number) =>
        calcLinearRating(v, 0.14, 4, 20);

    const getCentralDisplayRating = (v: string | number) =>
        calcLinearRating(v, 0.29, 7, 20);

    const getPassengerDisplayRating = (v: string | number) =>
        calcLinearRating(v, 0.23, 5, 15);

    const getDriverDisplayRating = (v: string | number) =>
        calcLinearRating(v, 0.25, 5, 15);

    const getDoorsRating = (v: string | number) =>
        calcLinearRating(v, 0.1, 2, 5);

    const getRimSizeRating = (v: string | number) =>
        calcLinearRating(v, 0.46, 355.6, 558.8);

    const getTireSpecificationRating = (v: any): string => {
        const s = Array.isArray(v) ? String(v[0]) : String(v);
        const beforeSlash = s.split("/")[0]?.replace(/(\d),(\d)/g, "$1$2").trim() ?? "";
        const width = parseFloat(beforeSlash);
        if (isNaN(width)) return "-";
        return calcLinearRating(width, 0.17, 155, 335);
    };

    const getChargingTimeAcRating = (v: string | number) =>
        // lower charging time is better → inverted
        calcLinearRating(v, 0.2, 1200, 240, true);

    const getChargingTimeDcRating = (v: string | number) =>
        // lower charging time is better → inverted
        calcLinearRating(v, 0.2, 80, 15, true);

    const getMaxChargingPowerAcRating = (v: string | number) =>
        calcLinearRating(v, 0.05, 3.7, 22);

    const getMaxChargingPowerDcRating = (v: string | number) =>
        calcLinearRating(v, 0.05, 40, 350);

    const getProductionYearRating = (v: any): string => {
        const year = parseNumericValue(v);
        if (isNaN(year)) return "-";
        const age = new Date().getFullYear() - year;
        if (age < 0 || age > 10) return "-";
        return (0.3 * ((10 - age) / 10)).toFixed(2);
    };

    // IF(gears=1, 0.2, 0.2×MAX(0,MIN((gears−4)/(10−4),1)))
    const getTransmissionGearsRating = (v: any): string => {
        const gears = parseNumericValue(v);
        if (isNaN(gears)) return "-";
        if (gears === 1) return (0.2).toFixed(2);
        return calcLinearRating(gears, 0.2, 4, 10);
    };

    const getSegmentRating = (
        length: any,
        width: any,
        height: any,
        wheelbase: any,
    ): string => {
        const l = parseNumericValue(length);
        const w = parseNumericValue(width);
        const h = parseNumericValue(height);
        const wb = parseNumericValue(wheelbase);

        if ([l, w, h, wb].some((n) => isNaN(n) || n === 0)) return "-";

        return (((l * w * h * wb) / 10000000000 / 7893.8) * 13.98).toFixed(2);
    };

    // Recompute and update the Length row's segment rating in-place whenever any dimension changes.
    const recomputeSegmentIfDimChanged = (updated: TableRow[], changedSpecName: string): void => {
        const sn = changedSpecName.trim().toLowerCase().replace(/\s+/g, "_");
        if (!DIMENSION_SNs.has(sn)) return;

        const getDimVal = (keys: string[]): any => {
            const r = updated.find((r2) => keys.includes(r2.rawSpecName.trim().toLowerCase().replace(/\s+/g, "_")));
            const v = r?.value;
            return v !== undefined && v !== "-" && v !== false ? v : undefined;
        };

        const lengthIdx = updated.findIndex((r2) => {
            const lSn = r2.rawSpecName.trim().toLowerCase().replace(/\s+/g, "_");
            return lSn === "length" || lSn === "length_(mm)";
        });

        if (lengthIdx !== -1) {
            updated[lengthIdx] = {
                ...updated[lengthIdx],
                rating: getSegmentRating(
                    getDimVal(["length", "length_(mm)"]),
                    getDimVal(["width", "width_(mm)"]),
                    getDimVal(["height", "height_(mm)"]),
                    getDimVal(["wheelbase", "wheelbase_(mm)"]),
                ),
            };
        }
    };

    // Recompute and update the Horse Power row's performance rating when hp or torque changes.
    const recomputePerformanceIfChanged = (updated: TableRow[], changedSpecName: string): void => {
        const sn = changedSpecName.trim().toLowerCase().replace(/\s+/g, "_");
        if (!PERFORMANCE_SNs.has(sn)) return;

        const getPerfVal = (keys: string[]): any => {
            const r = updated.find((r2) => keys.includes(r2.rawSpecName.trim().toLowerCase().replace(/\s+/g, "_")));
            const v = r?.value;
            return v !== undefined && v !== "-" && v !== false ? v : undefined;
        };

        const hpIdx = updated.findIndex((r2) => {
            const pSn = r2.rawSpecName.trim().toLowerCase().replace(/\s+/g, "_");
            return pSn === "horse_power" || pSn === "horse_power(hp)";
        });

        if (hpIdx !== -1) {
            updated[hpIdx] = {
                ...updated[hpIdx],
                rating: getPerformanceRating(
                    getPerfVal(["horse_power", "horse_power(hp)"]),
                    getPerfVal(["torque", "torque_(n.m)"]),
                ),
            };
        }
    };

    const getPerformanceRating = (horsepower: any, torque: any): string => {
        const hp = parseNumericValue(horsepower);
        const tq = parseNumericValue(torque);

        if (isNaN(hp) || hp === 0 || isNaN(tq) || tq === 0) return "-";

        return (((hp / 1914 + tq / 2360) / 2) * 25).toFixed(2);
    };

    

    const getNumCamerasRating = (value: any): string =>
        calcLinearRating(value, 0.04, 1, 12);

    const getNumUltrasonicRadarsRating = (value: any): string =>
        calcLinearRating(value, 0.03, 0, 12);

    const getNumLidarSensorsRating = (value: any): string =>
        calcLinearRating(value, 0.06, 0, 4);

    // Compute the rating for a spec the same way generateDataRowsFromJson does,
    // falling back to the ratings-list lookup for non-formula specs.
    const computeRatingForSpec = (
        specName: string,
        rawValue: any,
        subCatData: Record<string, any>,
    ): any => {
        const sn = specName.trim().toLowerCase().replace(/\s+/g, "_");
        if (sn === "length" || sn === "length_(mm)") {
            const width     = subCatData["width"]     || subCatData["width_(mm)"]     || undefined;
            const height    = subCatData["height"]    || subCatData["height_(mm)"]    || undefined;
            const wheelbase = subCatData["wheelbase"] || subCatData["wheelbase_(mm)"] || undefined;
            return getSegmentRating(rawValue, width, height, wheelbase);
        } else if (sn === "horse_power" || sn === "horse_power(hp)") {
            return getPerformanceRating(
                rawValue,
                subCatData["torque"] ?? subCatData["torque_(n.m)"] ?? subCatData["torque_(N.M)"],
            );
        } else if (sn === "displacement" || sn.startsWith("displacement")) {
            return getDisplacementRating(rawValue);
        } else if (sn === "0_100_km_h_acceleration" || (sn.includes("0") && sn.includes("100") && sn.includes("acceleration"))) {
            return getAccelerationRating(rawValue);
        } else if (sn === "top_speed" || sn.startsWith("top_speed")) {
            return getTopSpeedRating(rawValue);
        } else if (sn === "battery_capacity" || sn.startsWith("battery_capacity")) {
            return getBatteryCapacityRating(rawValue);
        } else if (sn === "fuel_tank_capacity" || sn.startsWith("fuel_tank_capacity")) {
            return getFuelTankRating(rawValue);
        } else if (sn === "running_cost_per_1km_in_aed" || sn.startsWith("running_cost")) {
            return getRunningCostRating(rawValue);
        } else if (sn === "number_of_seats" || sn.startsWith("number_of_seats")) {
            return getSeatsRating(rawValue);
        } else if (sn === "number_of_speakers") {
            return getSpeakersRating(rawValue);
        } else if (sn === "central_display_size" || sn.startsWith("central_display_size")) {
            return getCentralDisplayRating(rawValue);
        } else if (sn === "passenger_display_size" || sn.startsWith("passenger_display_size")) {
            return getPassengerDisplayRating(rawValue);
        } else if (sn === "driver_display_size" || sn.startsWith("driver_display_size")) {
            return getDriverDisplayRating(rawValue);
        } else if (sn === "number_of_doors") {
            return getDoorsRating(rawValue);
        } else if (sn === "rim_size" || sn.startsWith("rim_size")) {
            return getRimSizeRating(rawValue);
        } else if (sn === "tires_types_and_specification") {
            return getTireSpecificationRating(rawValue);
        } else if (sn === "charging_time_ac" || sn.startsWith("charging_time_ac")) {
            return getChargingTimeAcRating(rawValue);
        } else if (sn === "charging_time_dc" || sn.startsWith("charging_time_dc") || sn.startsWith("_charging_time_dc")) {
            return getChargingTimeDcRating(rawValue);
        } else if (sn === "max_charging_power_ac" || sn.startsWith("max_charging_power_ac")) {
            return getMaxChargingPowerAcRating(rawValue);
        } else if (sn === "max_charging_power_dc" || sn.startsWith("max_charging_power_dc")) {
            return getMaxChargingPowerDcRating(rawValue);
        } else if (sn === "production_year") {
            return getProductionYearRating(rawValue);
        } else if (sn === "transmission_gears" || sn.startsWith("transmission_gears")) {
            return getTransmissionGearsRating(rawValue);
        } else if (sn === "number_of_cameras") {
            return getNumCamerasRating(rawValue);
        } else if (sn === "number_of_ultrasonic_radars") {
            return getNumUltrasonicRadarsRating(rawValue);
        } else if (sn === "number_of_lidar_sensors") {
            return getNumLidarSensorsRating(rawValue);
        } else {
            return getRatingData(props.allRatings ?? [], specName, rawValue);
        }
    };

    const buildAuditResult = (index: number, row: TableRow, stored: StoredAuditData): AuditResult => ({
        isCorrect: stored.isCorrect,
        confidence: stored.confidence as AuditResult["confidence"],
        recommendedValue: stored.recommendedValue,
        reasoning: stored.reasoning,
        sourceUrls: stored.sourceUrls,
        rowInfo: { row, index, isEnumerated: stored.isEnumerated, currentValue: stored.currentValue, expectedValues: stored.expectedValues },
        onAccept: () => {
            const recVal = stored.recommendedValue;
            if (!recVal) return;
            const normalized = recVal.trim().toLowerCase() === "not relevant" ? false : recVal;
            const currentResult = resultRef.current;
            const subCatData: Record<string, any> = currentResult?.[row.rawMainCategory]?.[row.rawSubCategory] ?? {};
            const newRating = computeRatingForSpec(row.rawSpecName, normalized, subCatData);
            setTableData((prev) => {
                const updated = [...prev];
                const existingRating = updated[index].rating;
                updated[index] = { ...updated[index], value: normalized, rating: newRating !== "-" ? newRating : existingRating };
                recomputeSegmentIfDimChanged(updated, row.rawSpecName);
                recomputePerformanceIfChanged(updated, row.rawSpecName);
                return updated;
            });
            const specification = JSON.parse(JSON.stringify(resultRef.current ?? {}));
            if (!specification[row.rawMainCategory]) specification[row.rawMainCategory] = {};
            if (!specification[row.rawMainCategory][row.rawSubCategory]) specification[row.rawMainCategory][row.rawSubCategory] = {};
            specification[row.rawMainCategory][row.rawSubCategory][row.rawSpecName] = normalized;
            internalUpdate.current = true;
            props.onResultChange?.(specification);
        },
        onReject: () => {},
        onSaveAsOption: (value: string, andAccept?: boolean) => {
            if (andAccept) {
                const normalized = value.trim().toLowerCase() === "not relevant" ? false : value;
                pendingAcceptRef.current = { index, row, value: String(normalized) };
            }
            setNewOptionValue(value);
            setNewOptionRating("");
            setAddNewModal({ row, index });
        },
        onAddKnownValue: (value: string) => {
            const ratingMatch = (props.allRatings ?? []).find(
                (r) => r.key?.trim().toLowerCase() === row.rawSpecName.trim().toLowerCase() &&
                    r.value?.trim().toLowerCase() === value.trim().toLowerCase(),
            );
            const addedRating = parseFloat(ratingMatch?.rating) || 0;
            setTableData((prev) => {
                const updated = [...prev];
                const existingVal = String(updated[index].value ?? "-");
                const existingParts = existingVal !== "-" && existingVal !== ""
                    ? existingVal.split("|").map((v) => v.trim()).filter(Boolean) : [];
                if (existingParts.some((p) => p.toLowerCase() === value.trim().toLowerCase())) return prev;
                const newValue = existingParts.length > 0 ? [...existingParts, value].join(" | ") : value;
                const existingRating = parseFloat(String(updated[index].rating)) || 0;
                updated[index] = { ...updated[index], value: newValue, rating: (existingRating + addedRating).toFixed(2) };
                return updated;
            });
            const specification = JSON.parse(JSON.stringify(resultRef.current ?? {}));
            if (!specification[row.rawMainCategory]) specification[row.rawMainCategory] = {};
            if (!specification[row.rawMainCategory][row.rawSubCategory]) specification[row.rawMainCategory][row.rawSubCategory] = {};
            const existingSpecVal = specification[row.rawMainCategory][row.rawSubCategory][row.rawSpecName];
            const existingSpecParts = existingSpecVal && existingSpecVal !== "-"
                ? String(existingSpecVal).split("|").map((v: string) => v.trim()).filter(Boolean) : [];
            if (!existingSpecParts.some((p: string) => p.toLowerCase() === value.trim().toLowerCase())) {
                specification[row.rawMainCategory][row.rawSubCategory][row.rawSpecName] =
                    existingSpecParts.length > 0 ? [...existingSpecParts, value].join(" | ") : value;
                internalUpdate.current = true;
                props.onResultChange?.(specification);
            }
        },
    });

    const handleStopAudit = () => { auditStopRef.current = true; };

    const handleAuditAll = async () => {
        const toAudit = tableData
            .map((row, i) => ({ row, index: i }))
            .filter(({ row }) => !SKIP_AUDIT_SPECS.has(row.rawSpecName));
        if (toAudit.length === 0) return;

        auditStopRef.current = false;
        setAuditAllRunning(true);
        setAuditAllProgress({ done: 0, total: toAudit.length });
        setRowAuditData(new Map());
        setNeedsManualAudit(new Set());

        const newAuditData = new Map<number, StoredAuditData>();
        const newNeedsManual = new Set<number>();
        const BATCH = 10;

        for (let i = 0; i < toAudit.length; i += BATCH) {
            if (auditStopRef.current) break;

            const batch = toAudit.slice(i, i + BATCH);
            const settled = await Promise.allSettled(
                batch.map(async ({ row, index }) => {
                    const erDoc = props.expectedResults?.find((er) => er.key === row.rawMainCategory);
                    const subData = erDoc?.json?.[row.rawSubCategory];
                    const expectedVals: string[] = subData && Array.isArray(subData[row.rawSpecName]) ? subData[row.rawSpecName] : [];
                    const ratedVals: string[] = (props.allRatings ?? [])
                        .filter((r) => r.key?.trim().toLowerCase() === row.rawSpecName.trim().toLowerCase() && r.value)
                        .map((r) => r.value as string);
                    const allOptions = Array.from(new Set([...expectedVals, ...ratedVals]));
                    const isEnumerated = allOptions.length > 0;
                    const currentValue = String(row.value ?? "-");
                    const result = await API.aiAudit({
                        make, model, year: productionYear, vehicleTitle: props.vehicleTitle,
                        specName: row.rawSpecName, subCategory: row.rawSubCategory,
                        mainCategory: row.rawMainCategory, currentValue,
                        expectedValues: isEnumerated ? allOptions : undefined,
                    });
                    return { index, data: result.data, isEnumerated, currentValue, expectedValues: allOptions };
                }),
            );

            for (const outcome of settled) {
                if (outcome.status === "fulfilled") {
                    const { index, data, isEnumerated, currentValue, expectedValues } = outcome.value;
                    const stored: StoredAuditData = {
                        isCorrect: data.isCorrect,
                        confidence: data.confidence,
                        recommendedValue: normalizeRecommendedValue(data.recommendedValue ?? null, isEnumerated),
                        reasoning: data.reasoning,
                        sourceUrls: data.sourceUrls ?? [],
                        isEnumerated, currentValue, expectedValues,
                    };
                    newAuditData.set(index, stored);
                    if (!data.isCorrect) newNeedsManual.add(index);
                }
            }

            setAuditAllProgress((prev) => ({ ...prev, done: Math.min(prev.total, i + batch.length) }));
        }

        setRowAuditData(new Map(newAuditData));
        setNeedsManualAudit(new Set(newNeedsManual));
        setAuditAllRunning(false);
    };

    const handleAiAudit = async (index: number) => {
        const row = tableData[index];
        setAuditingIndices((prev) => new Set(prev).add(index));
        // Focus this row so the table highlight and Row Detail stay in sync with the AI Audit card
        setFocusedRowIndex(index);
        props.onRowFocus?.(row, index);

        // Compute isEnumerated for this row
        const erDoc = props.expectedResults?.find((er) => er.key === row.rawMainCategory);
        const subData = erDoc?.json?.[row.rawSubCategory];
        const expectedVals: string[] = subData && Array.isArray(subData[row.rawSpecName]) ? subData[row.rawSpecName] : [];
        const ratedVals: string[] = (props.allRatings ?? [])
            .filter((r) => r.key?.trim().toLowerCase() === row.rawSpecName.trim().toLowerCase() && r.value)
            .map((r) => r.value as string);
        const allOptions = Array.from(new Set([...expectedVals, ...ratedVals]));
        const isEnumerated = allOptions.length > 0;
        const currentValue = String(row.value ?? "-");

        // Fire immediately so the sidebar card appears with a loading state
        props.onAuditStart?.({ row, index, isEnumerated, currentValue, expectedValues: allOptions });

        try {
            const result = await API.aiAudit({
                make,
                model,
                year: productionYear,
                vehicleTitle: props.vehicleTitle,
                specName: row.rawSpecName,
                subCategory: row.rawSubCategory,
                mainCategory: row.rawMainCategory,
                currentValue,
                expectedValues: isEnumerated ? allOptions : undefined,
            });

            const data = result.data;
            const stored: StoredAuditData = {
                isCorrect: data.isCorrect,
                confidence: data.confidence,
                recommendedValue: normalizeRecommendedValue(data.recommendedValue ?? null, isEnumerated),
                reasoning: data.reasoning,
                sourceUrls: data.sourceUrls ?? [],
                isEnumerated,
                currentValue,
                expectedValues: allOptions,
            };
            setRowAuditData((prev) => new Map(prev).set(index, stored));
            if (!data.isCorrect) setNeedsManualAudit((prev) => new Set(prev).add(index));

            props.onAuditResult?.(buildAuditResult(index, row, stored));
        } catch (err) {
            console.error("AI Audit failed:", err);
        } finally {
            setAuditingIndices((prev) => {
                const next = new Set(prev);
                next.delete(index);
                return next;
            });
        }
    };

    const handleEdit = (index: number) => {
        setEditingIndex(index);
        const rawVal = tableData[index].value;
        setEditingText(!rawVal || rawVal === "-" || rawVal === false ? "" : String(rawVal));
        setEditingRating(tableData[index].rating !== "-" ? String(tableData[index].rating) : "");
    };

    const handleCancel = () => {
        setEditingIndex(null);
        setEditingText("");
        setEditingRating("");
        setNewOptionValue("");
        setNewOptionRating("");
    };

    const handleSaveEdit = (index: number) => {
        const updated = [...tableData];
        const row = updated[index];

        row.value = editingText || "-";
        row.rating = editingRating !== "" ? editingRating : "-";

        recomputeSegmentIfDimChanged(updated, row.rawSpecName);
        recomputePerformanceIfChanged(updated, row.rawSpecName);

        setTableData(updated);
        setEditingIndex(null);
        setOverrideScores(null);

        // Sync the updated value back to the result object so the parent stays in sync,
        // but flag the effect to skip re-processing so scroll and audit state are preserved.
        const specification = JSON.parse(JSON.stringify(props.result ?? {}));
        if (!specification[row.rawMainCategory]) specification[row.rawMainCategory] = {};
        if (!specification[row.rawMainCategory][row.rawSubCategory]) specification[row.rawMainCategory][row.rawSubCategory] = {};
        const valueToSave = editingText.trim().toLowerCase() === "not relevant" ? false : editingText;
        specification[row.rawMainCategory][row.rawSubCategory][row.rawSpecName] = valueToSave;
        internalUpdate.current = true;
        props.onResultChange?.(specification);
    };

    const handleClearValue = (index: number) => {
        const updated = [...tableData];
        const row = updated[index];
        updated[index] = { ...row, value: "-", rating: "-" };
        recomputeSegmentIfDimChanged(updated, row.rawSpecName);
        recomputePerformanceIfChanged(updated, row.rawSpecName);
        setTableData(updated);
        setOverrideScores(null);
        const specification = JSON.parse(JSON.stringify(props.result ?? {}));
        if (!specification[row.rawMainCategory]) specification[row.rawMainCategory] = {};
        if (!specification[row.rawMainCategory][row.rawSubCategory]) specification[row.rawMainCategory][row.rawSubCategory] = {};
        specification[row.rawMainCategory][row.rawSubCategory][row.rawSpecName] = "-";
        internalUpdate.current = true;
        props.onResultChange?.(specification);
    };

    useEffect(() => {
        resultRef.current = props.result;
        if (internalUpdate.current) {
            internalUpdate.current = false;
            return;
        }
        setOverrideScores(null);
        setAuditedIndices(new Set());
        setRowAuditData(new Map());
        setNeedsManualAudit(new Set());
        setAuditAllProgress({ done: 0, total: 0 });
        generateDataRowsFromJson(props.result);
    }, [props.result]);

    useEffect(() => {
        if (needsManualAudit.size > 0) {
            const done = Array.from(needsManualAudit).filter((i) => auditedIndices.has(i)).length;
            props.onAuditChange?.(done, needsManualAudit.size);
        } else if (auditAllProgress.total > 0 && !auditAllRunning) {
            props.onAuditChange?.(auditAllProgress.total, auditAllProgress.total);
        } else {
            props.onAuditChange?.(auditedIndices.size, tableData.length);
        }
    }, [auditedIndices, tableData.length, needsManualAudit, auditAllProgress, auditAllRunning]);

    useEffect(() => {
        if (!props.onScoresComputed || tableData.length === 0) return;
        const raw: Record<string, number> = { General: 0, Performance: 0, Safety: 0, Features: 0 };
        tableData.forEach((row) => {
            const v = parseFloat(row.rating);
            if (!isNaN(v) && row.uiGroup in raw) raw[row.uiGroup] += v;
        });
        const seg = (r: number, mid: number, maxRaw: number) =>
            Math.min(10, Math.round((r <= mid ? 1 + (r / mid) * 6 : 7 + ((r - mid) / (maxRaw - mid)) * 3) * 100) / 100);
        const g = overrideScores?.general     ?? seg(raw.General,     45,    49.15);
        const p = overrideScores?.performance ?? seg(raw.Performance,  30,    35.60);
        const s = overrideScores?.safety      ?? seg(raw.Safety,        4,     5.08);
        const f = overrideScores?.features    ?? seg(raw.Features,      8,    10.18);
        const overall   = overrideScores?.overall   ?? Math.round((g * 0.45 + p * 0.30 + s * 0.08 + f * 0.17) * 100) / 100;
        const customer  = overrideScores?.customer  ?? Math.min(10, Math.round(overall * 1.2 * 10) / 10);
        props.onScoresComputed({ general: g, performance: p, safety: s, features: f, overall, customer });
    }, [tableData, overrideScores]);

    const thStyle: React.CSSProperties = {
        padding: "8px 12px",
        fontSize: 11,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: 0.5,
        color: "#64748b",
        borderBottom: "1px solid #e2e8f0",
        whiteSpace: "nowrap",
    };

    const tdStyle: React.CSSProperties = {
        padding: "9px 12px",
        fontSize: 13,
        borderBottom: "1px solid #f1f5f9",
        verticalAlign: "middle",
    };

    const ratingColor = (r: any): string => {
        const v = parseFloat(r);
        if (isNaN(v)) return "#9ca3af";
        if (v >= 0.4) return "#16a34a";
        if (v >= 0.2) return "#ca8a04";
        return "#dc2626";
    };

    // MIN(10, ROUND(IF(raw<=mid, 1+(raw/mid)*6, 7+((raw-mid)/(maxRaw-mid))*3) * 1.1, 2))
    const calcCategoryScore = (raw: number, mid: number, maxRaw: number): number => {
        const base = raw <= mid
            ? 1 + (raw / mid) * 6
            : 7 + ((raw - mid) / (maxRaw - mid)) * 3;
        return Math.min(10, Math.round(base * 1.1 * 100) / 100);
    };

    const scoreColor = (s: number): string => {
        if (s >= 7) return "#16a34a";
        if (s >= 4) return "#ca8a04";
        return "#dc2626";
    };

    const REQUIRED_SPEC_KEYS = new Set(["make", "model", "production_year", "vehicle_type"]);

    const isRequiredMissing = (row: TableRow): boolean =>
        REQUIRED_SPEC_KEYS.has(row.rawSpecName) && row.value === "-";

    if (props.loading || processing) {
        return (
            <div style={{ padding: "48px 0", textAlign: "center", color: "#6b7280" }}>
                <div
                    className="spinner-border"
                    role="status"
                    style={{ width: 36, height: 36, borderWidth: 3, color: "#6366f1" }}
                />
                <p style={{ marginTop: 16, fontSize: 15, fontWeight: 500 }}>
                    {props.loading ? "Fetching specification..." : "Processing specification..."}
                </p>
                <p style={{ fontSize: 13, color: "#9ca3af", marginTop: 4 }}>
                    This may take a moment
                </p>
            </div>
        );
    }

    if (!props.result || tableData.length === 0) {
        return (
            <div style={{ padding: "48px 0", textAlign: "center", color: "#9ca3af" }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
                <p style={{ fontSize: 15, fontWeight: 500, color: "#6b7280" }}>
                    No specification available
                </p>
                <p style={{ fontSize: 13 }}>
                    No specification data was found for this vehicle.
                </p>
            </div>
        );
    }

    // Group rows by uiGroup, ordered by COMPARE_UI_GROUP_ORDER
    const groupMap = new Map<string, { row: TableRow; index: number }[]>();
    tableData.forEach((row, index) => {
        const g = row.uiGroup;
        if (!groupMap.has(g)) groupMap.set(g, []);
        groupMap.get(g)!.push({ row, index });
    });

    const grouped = [
        ...COMPARE_UI_GROUP_ORDER.filter((g) => groupMap.has(g)).map((g) => ({
            category: g,
            rows: groupMap.get(g)!,
        })),
        ...Array.from(groupMap.keys())
            .filter((g) => !(COMPARE_UI_GROUP_ORDER as readonly string[]).includes(g))
            .map((g) => ({ category: g, rows: groupMap.get(g)! })),
    ];

    // --- Overall scoring ---
    const rawScores: Record<string, number> = { General: 0, Performance: 0, Safety: 0, Features: 0 };
    tableData.forEach((row) => {
        const v = parseFloat(row.rating);
        if (!isNaN(v) && row.uiGroup in rawScores) {
            rawScores[row.uiGroup] += v;
        }
    });

    const twoSeg = (raw: number, mid: number, maxRaw: number): number =>
        Math.min(10, Math.round((raw <= mid ? 1 + (raw / mid) * 6 : 7 + ((raw - mid) / (maxRaw - mid)) * 3) * 100) / 100);

    const generalScore     = overrideScores?.general     ?? twoSeg(rawScores.General,     45,     49.15);
    const performanceScore = overrideScores?.performance ?? twoSeg(rawScores.Performance,  30,     35.60);
    const safetyScore      = overrideScores?.safety      ?? twoSeg(rawScores.Safety,        4,      5.08);
    const featuresScore    = overrideScores?.features    ?? twoSeg(rawScores.Features,      8,     10.18);
    const overallScore     = overrideScores?.overall     ?? Math.round((generalScore * 0.45 + performanceScore * 0.30 + safetyScore * 0.08 + featuresScore * 0.17) * 100) / 100;
    const finalScore       = overrideScores?.customer    ?? Math.min(10, Math.round(overallScore * 1.2 * 10) / 10);

    const categoryScores = [
        { label: "General",     score: generalScore,     raw: rawScores.General,     color: "#6366f1" },
        { label: "Performance", score: performanceScore,  raw: rawScores.Performance,  color: "#f97316" },
        { label: "Safety",      score: safetyScore,       raw: rawScores.Safety,       color: "#ef4444" },
        { label: "Features",    score: featuresScore,     raw: rawScores.Features,     color: "#16a34a" },
    ];

    return (
        <div style={{ fontFamily: "inherit" }}>

            {/* ── Score Panel ── */}
            <div style={{
                background: "#fff",
                border: "1px solid #e2e8f0",
                borderRadius: 12,
                padding: "20px 24px",
                marginBottom: 28,
                marginTop: 20
            }}>
                {/* Panel header */}
                <div style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    paddingBottom: 12,
                    borderBottom: "2px solid #e2e8f0",
                    marginBottom: 16,
                }}>
                    <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, color: "#6366f1" }}>
                        Vehicle Score Summary
                    </span>
                    <div style={{ display: "flex", gap: 6 }}>
                    </div>
                </div>

                {/* Category tiles */}
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
                    {categoryScores.map(({ label, score, raw, color }, ti) => {
                        const editKey = (["general", "performance", "safety", "features"] as const)[ti];
                        const editVal = editedScores[editKey];
                        const displayScore = editingScores ? (parseFloat(editVal) || 0) : score;
                        return (
                            <div key={label} style={{
                                flex: "1 1 130px",
                                background: "#f8fafc",
                                border: "1px solid #e2e8f0",
                                borderTop: `3px solid ${color}`,
                                borderRadius: 8,
                                padding: "12px 14px",
                            }}>
                                <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.8, color, marginBottom: 8 }}>
                                    {label}
                                </div>
                                {/* Progress bar */}
                                <div style={{ background: "#e2e8f0", borderRadius: 99, height: 5, marginBottom: 8, overflow: "hidden" }}>
                                    <div style={{
                                        width: `${Math.min(100, (displayScore / 10) * 100)}%`,
                                        height: "100%", borderRadius: 99,
                                        background: scoreColor(displayScore),
                                        transition: "width 0.4s ease",
                                    }} />
                                </div>
                                {editingScores ? (
                                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                        <input
                                            type="number"
                                            min={0} max={10} step={0.1}
                                            value={editVal}
                                            onChange={(e) => setEditedScores((prev) => ({ ...prev, [editKey]: e.target.value }))}
                                            style={{
                                                width: "100%", fontSize: 13, fontWeight: 700,
                                                border: "1px solid #e2e8f0", borderRadius: 6,
                                                padding: "3px 6px", color: scoreColor(displayScore),
                                                outline: "none",
                                            }}
                                        />
                                        <span style={{ fontSize: 11, color: "#9ca3af", whiteSpace: "nowrap" }}>/10</span>
                                    </div>
                                ) : (
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
                                        <span style={{ fontSize: 22, fontWeight: 800, color: scoreColor(score), lineHeight: 1 }}>
                                            {score.toFixed(1)}
                                            <span style={{ fontSize: 11, fontWeight: 500, color: "#9ca3af", marginLeft: 2 }}>/10</span>
                                        </span>
                                        <span style={{ fontSize: 10, color: "#9ca3af" }}>raw {raw.toFixed(2)}</span>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Overall + Customer Score row */}
                <div style={{
                    display: "flex", gap: 12, alignItems: "center",
                    background: "#f8fafc", border: "1px solid #e2e8f0",
                    borderRadius: 8, padding: "12px 16px",
                }}>
                    {/* Overall bar — always computed, not editable */}
                    <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.8, color: "#64748b", marginBottom: 6 }}>
                            Overall Score
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <div style={{ background: "#e2e8f0", borderRadius: 99, height: 6, flex: 1, overflow: "hidden" }}>
                                <div style={{
                                    width: `${Math.min(100, (overallScore / 10) * 100)}%`,
                                    height: "100%", borderRadius: 99,
                                    background: scoreColor(overallScore),
                                }} />
                            </div>
                            <span style={{ fontSize: 16, fontWeight: 700, color: scoreColor(overallScore), minWidth: 40, textAlign: "right" }}>
                                {overallScore.toFixed(2)}
                            </span>
                        </div>
                    </div>

                    <div style={{ width: 1, height: 40, background: "#e2e8f0" }} />

                    {/* Customer Score — editable */}
                    <div style={{ textAlign: "center", minWidth: 100 }}>
                        <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.8, color: "#64748b", marginBottom: 4 }}>
                            Customer Score
                        </div>
                        {editingScores ? (
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 4 }}>
                                <input
                                    type="number"
                                    min={0} max={10} step={0.1}
                                    value={editedScores.customerScore}
                                    onChange={(e) => setEditedScores((prev) => ({ ...prev, customerScore: e.target.value }))}
                                    style={{
                                        width: 64, fontSize: 18, fontWeight: 800, textAlign: "center",
                                        border: "1px solid #e2e8f0", borderRadius: 6,
                                        padding: "3px 4px",
                                        color: scoreColor(parseFloat(editedScores.customerScore) || 0),
                                        outline: "none",
                                    }}
                                />
                                <span style={{ fontSize: 12, color: "#9ca3af" }}>/10</span>
                            </div>
                        ) : (
                            <div style={{ display: "flex", alignItems: "baseline", justifyContent: "center", gap: 2 }}>
                                <span style={{ fontSize: 28, fontWeight: 900, color: scoreColor(finalScore), lineHeight: 1 }}>
                                    {finalScore.toFixed(1)}
                                </span>
                                <span style={{ fontSize: 12, color: "#9ca3af", fontWeight: 500 }}>/10</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            {/* ── Audit All toolbar ── */}
            {!props.readOnly && !props.isAudited && (
                <div style={{
                    display: "flex", flexDirection: "column" as const, gap: 8, marginBottom: 20,
                    padding: "10px 16px", background: "#fff",
                    border: "1px solid #e2e8f0", borderRadius: 10,
                }}>
                    {/* Row 1: progress / status + buttons */}
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                            {auditAllRunning && (
                                <>
                                    <span style={{ fontSize: 12, color: "#6366f1", fontWeight: 600, whiteSpace: "nowrap" as const }}>
                                        {auditAllProgress.done}/{auditAllProgress.total}
                                    </span>
                                    <div style={{ flex: 1, background: "#e2e8f0", borderRadius: 99, height: 6, overflow: "hidden" }}>
                                        <div style={{
                                            width: `${auditAllProgress.total > 0 ? (auditAllProgress.done / auditAllProgress.total) * 100 : 0}%`,
                                            height: "100%", borderRadius: 99,
                                            background: "#6366f1", transition: "width 0.3s ease",
                                        }} />
                                    </div>
                                </>
                            )}
                            {!auditAllRunning && needsManualAudit.size > 0 && (
                                <span style={{ fontSize: 13, fontWeight: 600, color: "#dc2626" }}>
                                    <i className="bi bi-exclamation-triangle-fill" style={{ marginRight: 5 }} />
                                    {needsManualAudit.size} row{needsManualAudit.size !== 1 ? "s" : ""} need manual review
                                </span>
                            )}
                            {!auditAllRunning && auditAllProgress.total > 0 && needsManualAudit.size === 0 && (
                                <span style={{ fontSize: 13, fontWeight: 600, color: "#16a34a" }}>
                                    <i className="bi bi-check-circle-fill" style={{ marginRight: 5 }} />
                                    All {auditAllProgress.total} rows verified
                                </span>
                            )}
                        </div>
                        {auditAllRunning && (
                            <button
                                onClick={handleStopAudit}
                                style={{
                                    display: "inline-flex", alignItems: "center", gap: 5,
                                    padding: "6px 14px", borderRadius: 8, fontSize: 13, fontWeight: 600,
                                    background: "transparent", color: "#dc2626",
                                    border: "1.5px solid #dc2626", cursor: "pointer",
                                    whiteSpace: "nowrap" as const,
                                }}
                            >
                                <i className="bi bi-stop-fill" /> Stop
                            </button>
                        )}
                        <button
                            onClick={handleAuditAll}
                            disabled={auditAllRunning}
                            style={{
                                display: "inline-flex", alignItems: "center", gap: 6,
                                padding: "6px 16px", borderRadius: 8, fontSize: 13, fontWeight: 600,
                                background: auditAllRunning ? "#e0e7ff" : "#6366f1",
                                color: auditAllRunning ? "#6366f1" : "#fff",
                                border: "none", cursor: auditAllRunning ? "not-allowed" : "pointer",
                                transition: "background 0.15s", whiteSpace: "nowrap" as const,
                            }}
                        >
                            <i className={auditAllRunning ? "bi bi-hourglass-split" : "bi bi-robot"} />
                            {auditAllRunning ? "Auditing…" : "Audit All"}
                        </button>
                    </div>

                    {/* Row 2: currently auditing specs (only while running) */}
                    {auditAllRunning && auditingIndices.size > 0 && (
                        <div style={{
                            display: "flex", alignItems: "flex-start", gap: 6,
                            padding: "6px 10px", borderRadius: 7,
                            background: "#f5f3ff", border: "1px solid #ddd6fe",
                        }}>
                            <i className="bi bi-robot" style={{ color: "#6366f1", fontSize: 12, marginTop: 1, flexShrink: 0 }} />
                            <div style={{ minWidth: 0 }}>
                                <span style={{ fontSize: 11, fontWeight: 700, color: "#6366f1", textTransform: "uppercase" as const, letterSpacing: 0.5, marginRight: 6 }}>
                                    Checking
                                </span>
                                <span style={{ fontSize: 12, color: "#4338ca", lineHeight: 1.5 }}>
                                    {Array.from(auditingIndices)
                                        .map((idx) => tableData[idx]?.specName)
                                        .filter(Boolean)
                                        .join(", ")}
                                </span>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Total row count */}
            <div style={{ fontSize: 12, color: "#64748b", marginBottom: 12, textAlign: "right" }}>
                <span style={{ fontWeight: 600, color: "#1e293b" }}>{tableData.length}</span> specification{tableData.length !== 1 ? "s" : ""} total
            </div>

            {/* Grouped sections */}
            {grouped.map(({ category, rows: groupRows }) => (
                <div key={category} style={{ marginBottom: 28 }}>
                    {/* Section label */}
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            fontSize: 11,
                            fontWeight: 700,
                            textTransform: "uppercase",
                            letterSpacing: 1,
                            color: "#6366f1",
                            padding: "6px 4px",
                            borderBottom: "2px solid #e2e8f0",
                            marginBottom: 0,
                        }}
                    >
                        <span>{category}</span>
                        <span style={{ fontWeight: 500, color: "#94a3b8", letterSpacing: 0 }}>
                            {groupRows.length} row{groupRows.length !== 1 ? "s" : ""}
                        </span>
                    </div>

                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead>
                            <tr style={{ background: "#f8fafc" }}>
                                <th style={thStyle}>#</th>
                                <th style={{ ...thStyle, width: "30%" }}>Specification</th>
                                <th style={{ ...thStyle, width: "28%" }}>Sub-category</th>
                                <th style={{ ...thStyle }}>Value</th>
                                <th style={{ ...thStyle, textAlign: "center" }}>Rating</th>
                                {!props.readOnly && !props.isAudited && <th style={{ ...thStyle, textAlign: "center", width: 76 }}>Action</th>}
                                {!props.readOnly && !props.isAudited && <th style={{ ...thStyle, textAlign: "center", width: 50 }}>AI Audit</th>}
                                {!props.readOnly && !props.isAudited && <th style={{ ...thStyle, textAlign: "center", width: 50 }}>Audited</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {groupRows.map(({ row, index }, i) => {
                                const missing = isRequiredMissing(row);
                                const needsReview = needsManualAudit.has(index);
                                const baseBg = missing ? "#fff5f5"
                                    : needsReview ? "#fff7ed"
                                    : i % 2 === 0 ? "#fff" : "#f8fafc";
                                const hoverBg = missing ? "#ffe4e4" : needsReview ? "#ffedd5" : "#eef2ff";
                                const isFocused = focusedRowIndex === index;
                                return (
                                <tr
                                    key={index}
                                    style={{
                                        background: baseBg,
                                        transition: "background 0.15s",
                                        borderLeft: isFocused
                                            ? "3px solid #6366f1"
                                            : missing ? "3px solid #ef4444"
                                            : needsReview ? "3px solid #f97316"
                                            : "3px solid transparent",
                                        cursor: "pointer",
                                    }}
                                    onClick={() => {
                                        setFocusedRowIndex(index);
                                        props.onRowFocus?.(row, index);
                                        const stored = rowAuditData.get(index);
                                        if (stored) {
                                            props.onAuditStart?.({ row, index, isEnumerated: stored.isEnumerated, currentValue: stored.currentValue, expectedValues: stored.expectedValues });
                                            props.onAuditResult?.(buildAuditResult(index, row, stored));
                                        }
                                    }}
                                    onMouseEnter={(e) =>
                                        (e.currentTarget.style.background = hoverBg)
                                    }
                                    onMouseLeave={(e) =>
                                        (e.currentTarget.style.background = baseBg)
                                    }
                                >
                                    <td style={{ ...tdStyle, color: "#9ca3af", fontSize: 12, width: 36 }}>
                                        {i + 1}
                                    </td>

                                    <td style={tdStyle}>
                                        <span style={{ fontWeight: 600, color: "#1e293b", fontSize: 14 }}>
                                            {row.specName}
                                        </span>
                                    </td>

                                    <td style={tdStyle}>
                                        <span
                                            style={{
                                                fontSize: 12,
                                                color: "#64748b",
                                                background: "#f1f5f9",
                                                padding: "2px 8px",
                                                borderRadius: 4,
                                            }}
                                        >
                                            {row.subCategory}
                                        </span>
                                    </td>

                                    <td style={tdStyle}>
                                        {editingIndex === index ? (() => {
                                            // Determine dropdown options for this row
                                            const erDoc = props.expectedResults?.find(er => er.key === row.rawMainCategory);
                                            const subData = erDoc?.json?.[row.rawSubCategory];
                                            const expectedVals: string[] = subData && Array.isArray(subData[row.rawSpecName]) ? subData[row.rawSpecName] : [];
                                            const ratedVals: string[] = (props.allRatings ?? [])
                                                .filter(r => r.key?.trim().toLowerCase() === row.rawSpecName.trim().toLowerCase() && r.value)
                                                .map(r => r.value as string);
                                            const options = Array.from(new Set([...expectedVals, ...ratedVals]));
                                            const isEnumerated = options.length > 0;

                                            const selectedVals = editingText
                                                ? editingText.split("|").map(v => v.trim()).filter(v => v && v !== "-")
                                                : [];

                                            const toggleOption = (opt: string) => {
                                                const current = new Set(selectedVals);
                                                if (current.has(opt)) current.delete(opt);
                                                else current.add(opt);
                                                const newText = Array.from(current).join(" | ");
                                                setEditingText(newText);
                                                // Sum ratings for all selected values
                                                let total = 0;
                                                for (const v of Array.from(current)) {
                                                    const m = (props.allRatings ?? []).find(r =>
                                                        r.key?.trim().toLowerCase() === row.rawSpecName.trim().toLowerCase() &&
                                                        r.value?.trim().toLowerCase() === v.trim().toLowerCase()
                                                    );
                                                    if (m) total += parseFloat(m.rating) || 0;
                                                }
                                                if (total > 0) {
                                                    setEditingRating(total.toFixed(2));
                                                } else {
                                                    // Fallback: formula-based rating for numeric specs
                                                    const subCatData = {
                                                        ...(resultRef.current?.[row.rawMainCategory]?.[row.rawSubCategory] ?? {}),
                                                        [row.rawSpecName]: newText,
                                                    };
                                                    const computed = computeRatingForSpec(row.rawSpecName, newText, subCatData);
                                                    setEditingRating(computed && computed !== "-" ? String(computed) : "");
                                                }
                                            };

                                            if (isEnumerated) {
                                                return (
                                                    <div style={{
                                                        border: "1px solid #ced4da",
                                                        borderRadius: 6,
                                                        background: "#fff",
                                                        maxHeight: 180,
                                                        overflowY: "auto",
                                                        fontSize: 13,
                                                    }}>
                                                        {options.map((opt) => (
                                                            <label key={opt} style={{
                                                                display: "flex",
                                                                alignItems: "center",
                                                                gap: 8,
                                                                padding: "5px 10px",
                                                                cursor: "pointer",
                                                                borderBottom: "1px solid #f1f5f9",
                                                                margin: 0,
                                                                background: selectedVals.includes(opt) ? "#eff6ff" : "transparent",
                                                            }}>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={selectedVals.includes(opt)}
                                                                    onChange={() => toggleOption(opt)}
                                                                    style={{ accentColor: "#6366f1", width: 14, height: 14, cursor: "pointer" }}
                                                                />
                                                                <span style={{ color: selectedVals.includes(opt) ? "#3b82f6" : "#374151" }}>
                                                                    {opt}
                                                                </span>
                                                            </label>
                                                        ))}
                                                        <button
                                                            onClick={() => { setNewOptionValue(""); setNewOptionRating(""); setAddNewModal({ row, index }); }}
                                                            style={{
                                                                display: "block",
                                                                width: "100%",
                                                                padding: "5px 10px",
                                                                textAlign: "left",
                                                                background: "none",
                                                                border: "none",
                                                                fontSize: 12,
                                                                color: "#6366f1",
                                                                cursor: "pointer",
                                                            }}
                                                        >
                                                            ＋ Add new option…
                                                        </button>
                                                    </div>
                                                );
                                            }

                                            // Free text field
                                            return (
                                                <input
                                                    type="text"
                                                    className="form-control form-control-sm"
                                                    value={editingText}
                                                    onChange={(e) => {
                                                        const newVal = e.target.value;
                                                        setEditingText(newVal);
                                                        const subCatData = {
                                                            ...(props.result?.[row.rawMainCategory]?.[row.rawSubCategory] ?? {}),
                                                            [row.rawSpecName]: newVal,
                                                        };
                                                        const computed = computeRatingForSpec(row.rawSpecName, newVal, subCatData);
                                                        if (computed !== null && computed !== undefined && computed !== "-" && computed !== "") {
                                                            setEditingRating(String(computed));
                                                        }

                                                        const editedSn = row.rawSpecName.trim().toLowerCase().replace(/\s+/g, "_");

                                                        // Live-update the Length row's segment rating while typing any other dimension
                                                        const isNonLengthDim = DIMENSION_SNs.has(editedSn) && editedSn !== "length" && editedSn !== "length_(mm)";
                                                        if (isNonLengthDim) {
                                                            setTableData((prev) => {
                                                                const liveUpdated = [...prev];
                                                                const getDimValLive = (keys: string[]): any => {
                                                                    if (keys.includes(editedSn)) return newVal || undefined;
                                                                    const r = liveUpdated.find((r2) => keys.includes(r2.rawSpecName.trim().toLowerCase().replace(/\s+/g, "_")));
                                                                    const v = r?.value;
                                                                    return v !== undefined && v !== "-" && v !== false ? v : undefined;
                                                                };
                                                                const lengthIdxLive = liveUpdated.findIndex((r2) => {
                                                                    const lSn = r2.rawSpecName.trim().toLowerCase().replace(/\s+/g, "_");
                                                                    return lSn === "length" || lSn === "length_(mm)";
                                                                });
                                                                if (lengthIdxLive !== -1) {
                                                                    const segRating = getSegmentRating(
                                                                        getDimValLive(["length", "length_(mm)"]),
                                                                        getDimValLive(["width", "width_(mm)"]),
                                                                        getDimValLive(["height", "height_(mm)"]),
                                                                        getDimValLive(["wheelbase", "wheelbase_(mm)"]),
                                                                    );
                                                                    if (liveUpdated[lengthIdxLive].rating !== segRating) {
                                                                        liveUpdated[lengthIdxLive] = { ...liveUpdated[lengthIdxLive], rating: segRating };
                                                                        return liveUpdated;
                                                                    }
                                                                }
                                                                return prev;
                                                            });
                                                        }

                                                        // Live-update the Horse Power row's performance rating while typing torque
                                                        const isNonHpPerf = PERFORMANCE_SNs.has(editedSn) && editedSn !== "horse_power" && editedSn !== "horse_power(hp)";
                                                        if (isNonHpPerf) {
                                                            setTableData((prev) => {
                                                                const liveUpdated = [...prev];
                                                                const getPerfValLive = (keys: string[]): any => {
                                                                    if (keys.includes(editedSn)) return newVal || undefined;
                                                                    const r = liveUpdated.find((r2) => keys.includes(r2.rawSpecName.trim().toLowerCase().replace(/\s+/g, "_")));
                                                                    const v = r?.value;
                                                                    return v !== undefined && v !== "-" && v !== false ? v : undefined;
                                                                };
                                                                const hpIdxLive = liveUpdated.findIndex((r2) => {
                                                                    const pSn = r2.rawSpecName.trim().toLowerCase().replace(/\s+/g, "_");
                                                                    return pSn === "horse_power" || pSn === "horse_power(hp)";
                                                                });
                                                                if (hpIdxLive !== -1) {
                                                                    const perfRating = getPerformanceRating(
                                                                        getPerfValLive(["horse_power", "horse_power(hp)"]),
                                                                        getPerfValLive(["torque", "torque_(n.m)"]),
                                                                    );
                                                                    if (liveUpdated[hpIdxLive].rating !== perfRating) {
                                                                        liveUpdated[hpIdxLive] = { ...liveUpdated[hpIdxLive], rating: perfRating };
                                                                        return liveUpdated;
                                                                    }
                                                                }
                                                                return prev;
                                                            });
                                                        }
                                                    }}
                                                    style={{ fontSize: 13 }}
                                                    autoFocus
                                                />
                                            );
                                        })() : (
                                            <span style={{ fontSize: 14, color: "#374151" }}>
                                                {row.value === false ? "-" : row.value}
                                            </span>
                                        )}
                                    </td>

                                    <td style={{ ...tdStyle, textAlign: "center" }}>
                                        {editingIndex === index ? (
                                            <input
                                                type="number"
                                                min={0}
                                                step={0.01}
                                                value={editingRating}
                                                onChange={(e) => setEditingRating(e.target.value)}
                                                style={{
                                                    width: 72, fontSize: 12, fontWeight: 700,
                                                    textAlign: "center", border: "1px solid #e2e8f0",
                                                    borderRadius: 6, padding: "3px 4px", outline: "none",
                                                }}
                                                placeholder="—"
                                            />
                                        ) : row.rating !== "-" ? (
                                            <span
                                                style={{
                                                    display: "inline-block",
                                                    minWidth: 52,
                                                    padding: "3px 10px",
                                                    borderRadius: 20,
                                                    fontSize: 12,
                                                    fontWeight: 700,
                                                    background: ratingColor(row.rating) + "18",
                                                    color: ratingColor(row.rating),
                                                    border: `1px solid ${ratingColor(row.rating)}40`,
                                                }}
                                            >
                                                {row.rating}
                                            </span>
                                        ) : (
                                            <span style={{ color: "#d1d5db", fontSize: 13 }}>—</span>
                                        )}
                                    </td>

                                    {!props.readOnly && !props.isAudited && <td style={{ ...tdStyle, textAlign: "center" }} onClick={(e) => e.stopPropagation()}>
                                        {editingIndex === index ? (
                                            <div style={{ display: "flex", gap: 4, justifyContent: "center" }}>
                                                <button
                                                    title="Save"
                                                    onClick={() => handleSaveEdit(index)}
                                                    style={{
                                                        width: 28, height: 28, borderRadius: "50%", padding: 0, border: "none",
                                                        background: "#16a34a", color: "#fff", cursor: "pointer",
                                                        display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 13,
                                                    }}
                                                >
                                                    <i className="bi bi-check-lg" />
                                                </button>
                                                <button
                                                    title="Cancel"
                                                    onClick={handleCancel}
                                                    style={{
                                                        width: 28, height: 28, borderRadius: "50%", padding: 0,
                                                        border: "1.5px solid #9ca3af", background: "transparent",
                                                        color: "#6b7280", cursor: "pointer",
                                                        display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 13,
                                                    }}
                                                >
                                                    <i className="bi bi-x-lg" />
                                                </button>
                                            </div>
                                        ) : (
                                            <div style={{ display: "flex", gap: 4, justifyContent: "center" }}>
                                                <button
                                                    title="Edit"
                                                    onClick={() => handleEdit(index)}
                                                    style={{
                                                        width: 28, height: 28, borderRadius: "50%", padding: 0,
                                                        border: "1.5px solid #6366f1", background: "transparent",
                                                        color: "#6366f1", cursor: "pointer",
                                                        display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 13,
                                                    }}
                                                >
                                                    <i className="bi bi-pencil" />
                                                </button>
                                                <button
                                                    title="Clear value"
                                                    onClick={() => handleClearValue(index)}
                                                    style={{
                                                        width: 28, height: 28, borderRadius: "50%", padding: 0,
                                                        border: "1.5px solid #f97316", background: "transparent",
                                                        color: "#f97316", cursor: "pointer",
                                                        display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 13,
                                                    }}
                                                >
                                                    <i className="bi bi-eraser" />
                                                </button>
                                            </div>
                                        )}
                                    </td>}

                                    {/* AI Audit */}
                                    {!props.readOnly && !props.isAudited && <td style={{ ...tdStyle, textAlign: "center" }} onClick={(e) => e.stopPropagation()}>
                                        <button
                                            title={auditingIndices.has(index) ? "Auditing…" : "Run AI Audit"}
                                            disabled={auditingIndices.has(index)}
                                            onClick={() => handleAiAudit(index)}
                                            style={{
                                                width: 28, height: 28, borderRadius: "50%", padding: 0,
                                                border: "1.5px solid #6366f1",
                                                background: auditingIndices.has(index) ? "#6366f1" : "transparent",
                                                color: auditingIndices.has(index) ? "#fff" : "#6366f1",
                                                cursor: auditingIndices.has(index) ? "not-allowed" : "pointer",
                                                display: "inline-flex", alignItems: "center", justifyContent: "center",
                                                fontSize: 13, transition: "all 0.15s",
                                            }}
                                        >
                                            <i className={auditingIndices.has(index) ? "bi bi-hourglass-split" : "bi bi-robot"} />
                                        </button>
                                    </td>}

                                    {/* Audited checkmark — only on rows needing manual review (or all rows before audit-all runs) */}
                                    {!props.readOnly && !props.isAudited && (needsManualAudit.size === 0 || needsManualAudit.has(index)) && <td style={{ ...tdStyle, textAlign: "center" }} onClick={(e) => e.stopPropagation()}>
                                        <button
                                            title={auditedIndices.has(index) ? "Audited — click to undo" : "Mark as audited"}
                                            onClick={() => {
                                                setAuditedIndices((prev) => {
                                                    const next = new Set(prev);
                                                    if (next.has(index)) next.delete(index);
                                                    else next.add(index);
                                                    return next;
                                                });
                                            }}
                                            style={{
                                                width: 28,
                                                height: 28,
                                                borderRadius: "50%",
                                                border: auditedIndices.has(index) ? "2px solid #16a34a" : "2px solid #d1d5db",
                                                background: auditedIndices.has(index) ? "#16a34a" : "transparent",
                                                color: auditedIndices.has(index) ? "#fff" : "#d1d5db",
                                                cursor: "pointer",
                                                display: "inline-flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                fontSize: 14,
                                                transition: "all 0.15s",
                                                padding: 0,
                                            }}
                                        >
                                            ✓
                                        </button>
                                    </td>}
                                </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            ))}

            {/* ── Add New Option Modal ── */}
            <Modal show={!!addNewModal} onHide={() => setAddNewModal(null)} centered size="sm">
                <Modal.Header closeButton>
                    <Modal.Title style={{ fontSize: 15, fontWeight: 700 }}>
                        Add New Option
                        {addNewModal && (
                            <div style={{ fontSize: 12, fontWeight: 400, color: "#64748b", marginTop: 2 }}>
                                {addNewModal.row.specName}
                            </div>
                        )}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>Value</Form.Label>
                        <Form.Control
                            type="text"
                            size="sm"
                            placeholder="e.g. Petrol"
                            value={newOptionValue}
                            onChange={(e) => setNewOptionValue(e.target.value)}
                            autoFocus
                        />
                    </Form.Group>
                    <Form.Group>
                        <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>Rating <span style={{ color: "#9ca3af", fontWeight: 400 }}>(0 – 1)</span></Form.Label>
                        <Form.Control
                            type="number"
                            size="sm"
                            placeholder="e.g. 0.45"
                            min={0}
                            max={1}
                            step={0.01}
                            value={newOptionRating}
                            onChange={(e) => setNewOptionRating(e.target.value)}
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" size="sm" onClick={() => setAddNewModal(null)}>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        size="sm"
                        disabled={addingNewLoading || !newOptionValue.trim()}
                        onClick={async () => {
                            if (!addNewModal) return;
                            setAddingNewLoading(true);
                            try {
                                const rating: any = await API.createRating({
                                    key: addNewModal.row.rawSpecName,
                                    value: newOptionValue.trim(),
                                    rating: parseFloat(newOptionRating) || 0,
                                    category: addNewModal.row.rawMainCategory,
                                    subCategory: addNewModal.row.rawSubCategory,
                                });
                                props.onRatingCreated?.(rating.data ?? {
                                    key: addNewModal.row.rawSpecName,
                                    value: newOptionValue.trim(),
                                    rating: parseFloat(newOptionRating) || 0,
                                });

                                // Also push the new value into the matching expected-result document
                                const rawMain = addNewModal.row.rawMainCategory.trim().toLowerCase();
                                const erDoc = props.expectedResults?.find(
                                    (e) => String(e.key).trim().toLowerCase() === rawMain,
                                );
                                if (erDoc) {
                                    const updatedJson = JSON.parse(JSON.stringify(erDoc.json ?? {}));

                                    // Resolve sub-category key (exact first, then case-insensitive)
                                    const rawSubCat = addNewModal.row.rawSubCategory;
                                    const rawSpec   = addNewModal.row.rawSpecName;
                                    const subCat =
                                        rawSubCat in updatedJson
                                            ? rawSubCat
                                            : Object.keys(updatedJson).find(
                                                  (k) => k.toLowerCase() === rawSubCat.toLowerCase(),
                                              ) ?? rawSubCat;

                                    if (!updatedJson[subCat]) updatedJson[subCat] = {};

                                    // Resolve spec key (exact first, then case-insensitive)
                                    const specKey =
                                        rawSpec in updatedJson[subCat]
                                            ? rawSpec
                                            : Object.keys(updatedJson[subCat]).find(
                                                  (k) => k.toLowerCase() === rawSpec.toLowerCase(),
                                              ) ?? rawSpec;

                                    const existing = updatedJson[subCat][specKey];
                                    if (Array.isArray(existing)) {
                                        if (!existing.map((v: string) => v.toLowerCase()).includes(newOptionValue.trim().toLowerCase())) {
                                            updatedJson[subCat][specKey] = [...existing, newOptionValue.trim()];
                                        }
                                    } else {
                                        updatedJson[subCat][specKey] = [newOptionValue.trim()];
                                    }

                                    try {
                                        await API.updateExpectedResult(erDoc._id, { json: updatedJson });
                                        props.onExpectedResultUpdated?.({ ...erDoc, json: updatedJson });
                                    } catch (e) {
                                        console.error("Failed to update expected result:", e);
                                    }
                                }

                                // If this modal was opened from an AI Audit "accept" flow,
                                // apply the value to the row now that the rating exists.
                                const pending = pendingAcceptRef.current;
                                if (pending) {
                                    pendingAcceptRef.current = null;
                                    const subCatData: Record<string, any> =
                                        props.result?.[pending.row.rawMainCategory]?.[pending.row.rawSubCategory] ?? {};
                                    const savedRating = parseFloat(newOptionRating) || 0;
                                    setTableData((prev) => {
                                        const updated = [...prev];
                                        const existingVal = String(updated[pending.index].value ?? "-");
                                        // Append to pipe list for multi-value fields; replace otherwise
                                        const existingParts = existingVal !== "-" && existingVal !== ""
                                            ? existingVal.split("|").map((v) => v.trim()).filter(Boolean)
                                            : [];
                                        const alreadyPresent = existingParts.some(
                                            (p) => p.toLowerCase() === pending.value.toLowerCase(),
                                        );
                                        const newValue = alreadyPresent
                                            ? existingVal
                                            : existingParts.length > 0
                                                ? [...existingParts, pending.value].join(" | ")
                                                : pending.value;
                                        // Sum existing rating with the new one for multi-value fields
                                        const existingRating = parseFloat(String(updated[pending.index].rating)) || 0;
                                        const combinedRating = existingParts.length > 0 && !alreadyPresent
                                            ? (existingRating + savedRating).toFixed(2)
                                            : savedRating > 0
                                                ? savedRating.toFixed(2)
                                                : computeRatingForSpec(pending.row.rawSpecName, newValue, subCatData);
                                        updated[pending.index] = {
                                            ...updated[pending.index],
                                            value: newValue,
                                            rating: combinedRating,
                                        };
                                        return updated;
                                    });
                                    const specification = JSON.parse(JSON.stringify(props.result ?? {}));
                                    if (!specification[pending.row.rawMainCategory]) specification[pending.row.rawMainCategory] = {};
                                    if (!specification[pending.row.rawMainCategory][pending.row.rawSubCategory]) specification[pending.row.rawMainCategory][pending.row.rawSubCategory] = {};
                                    const existingSpecVal = specification[pending.row.rawMainCategory][pending.row.rawSubCategory][pending.row.rawSpecName];
                                    const existingSpecParts = existingSpecVal && existingSpecVal !== "-"
                                        ? String(existingSpecVal).split("|").map((v: string) => v.trim()).filter(Boolean)
                                        : [];
                                    const alreadyInSpec = existingSpecParts.some(
                                        (p: string) => p.toLowerCase() === pending.value.toLowerCase(),
                                    );
                                    specification[pending.row.rawMainCategory][pending.row.rawSubCategory][pending.row.rawSpecName] =
                                        alreadyInSpec
                                            ? existingSpecVal
                                            : existingSpecParts.length > 0
                                                ? [...existingSpecParts, pending.value].join(" | ")
                                                : pending.value;
                                    internalUpdate.current = true;
                                    props.onResultChange?.(specification);
                                } else {
                                    // Normal flow: populate the edit row
                                    setEditingText(newOptionValue.trim());
                                    setEditingRating(newOptionRating);
                                }
                                setAddNewModal(null);
                                setNewOptionValue("");
                                setNewOptionRating("");
                            } catch (err) {
                                console.error("Failed to create rating:", err);
                            } finally {
                                setAddingNewLoading(false);
                            }
                        }}
                    >
                        {addingNewLoading ? "Adding…" : "Add Option"}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default MatchSpecificationOutput;
